import React, { useCallback, useState } from 'react';
import type { ToastProps } from '@components/main-components/elements/Toast';
import { Toast } from '@components/main-components/elements/Toast';
import { useAudioController } from '@store/audio/AudioController';
import { createContainer } from '../utils/unstated';

export type OpenToastProps = Omit<ToastProps, 'isToastOpen' | 'setToastOpen'>;

export const ToastContext = createContainer(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [toastProps, setToastProps] = useState<OpenToastProps | null>(null);

  const openToast = useCallback(
    (newToastProps: OpenToastProps) => {
      setToastProps(newToastProps);
      setIsOpen(true);
    },
    [setIsOpen, setToastProps],
  );

  const closeToast = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return {
    isOpen,
    toastProps,
    openToast,
    closeToast,
  };
});

export function useToast() {
  return ToastContext.useContainer();
}

export const ToastContainer = () => {
  const { isOpen, toastProps, closeToast } = ToastContext.useContainer();
  const { activeTrackId, hideExpandedPlayer } = useAudioController();

  const handleOpenChange = useCallback(() => {
    if (isOpen) closeToast();
  }, [closeToast, isOpen]);

  if (toastProps === null) return null;

  return (
    <Toast
      {...toastProps}
      isToastOpen={isOpen}
      setToastOpen={handleOpenChange}
      withAudioPlayer={!!activeTrackId}
      inverted={!hideExpandedPlayer}
      withExpandedAudioPlayer={!hideExpandedPlayer}
    />
  );
};
